import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  Search, Plus, X, Save, 
  FileText, Printer, Eye, 
  Sparkles, Bold, Italic, Underline, 
  AlignLeft, AlignCenter, AlignRight, ArrowLeft, FileDown,
  Loader2, Smartphone, Mail, MapPin, UserCheck, CreditCard,
  // Fix: Added missing icons
  DollarSign, Calendar
} from 'lucide-react';
import { Contract, ContractStatus, Client } from './types';
import { Card, Badge } from './SharedComponents';

const printStyles = `
  @media print {
    body { background: white !important; margin: 0 !important; padding: 0 !important; }
    .no-print { display: none !important; }
    @page { size: A4; margin: 0; }
  }

  .a4-preview-container { padding: 40px 0; background: rgba(0,0,0,0.05); }
  
  .a4-preview {
    width: 210mm;
    min-height: 297mm;
    padding: 25mm;
    margin: 0 auto;
    background: white;
    box-shadow: 0 40px 100px rgba(0,0,0,0.2);
    font-family: 'Inter', serif;
    color: #1e293b;
    border-radius: 4px;
    border: 1px solid #f1f5f9;
  }
`;

export const ContractsPage = ({ clients, contracts = [], onUpdateContracts }: { clients: Client[], contracts: Contract[], onUpdateContracts: (c: Contract[]) => void }) => {
  const [search, setSearch] = useState('');
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);

  // Template base robusto com as novas variáveis dinâmicas
  useEffect(() => {
    if (contracts.length === 0) {
      const template: Contract = { 
        id: 'tpl-1', 
        clientId: '', 
        subject: 'MODELO: Locação de Estruturas Metálicas v2', 
        value: 0, 
        type: 'Template', 
        startDate: new Date().toISOString().split('T')[0], 
        endDate: '', 
        description: `
          <h1 style="text-align: center; text-transform: uppercase; font-family: sans-serif; color: #000;">Contrato de Locação e Prestação de Serviços</h1>
          <hr style="border: 0; border-top: 1px solid #eee; margin: 30px 0;" />
          
          <h3 style="font-size: 12pt; text-transform: uppercase;">1. DAS PARTES</h3>
          <p style="font-size: 11pt; text-align: justify;">
            <strong>CONTRATADA:</strong> EVENTLOG SISTEMAS DE LOGISTICA LTDA, sediada em Sorocaba/SP, inscrita no CNPJ sob nº 12.345.678/0001-90. <br/><br/>
            <strong>CONTRATANTE:</strong> <strong>{{CLIENTE}}</strong>, com documento <strong>{{DOCUMENTO}}</strong>, 
            contato via e-mail <strong>{{EMAIL}}</strong> ou telefone <strong>{{CELULAR}}</strong>, 
            localizado no endereço: <strong>{{ENDERECO}}</strong>. <br/>
            Aos cuidados do responsável: <strong>{{RESPONSAVEL}}</strong>.
          </p>

          <h3 style="font-size: 12pt; text-transform: uppercase; margin-top: 20px;">2. DO OBJETO E VALOR</h3>
          <p style="font-size: 11pt; text-align: justify;">O presente contrato tem por finalidade a locação de estruturas temporárias para o projeto <strong>{{ASSUNTO}}</strong>.</p>
          <p style="font-size: 11pt; text-align: justify;">Pela execução dos serviços descritos, o CONTRATANTE pagará a importância total de <strong>{{VALOR}}</strong>.</p>
          
          <br/><br/>
          <p style="text-align: right; font-size: 11pt;">Sorocaba, {{DATA}}</p>
          <br/><br/><br/>
          
          <div style="display: flex; justify-content: space-between; gap: 50px; margin-top: 50px;">
            <div style="flex: 1; border-top: 1px solid #000; text-align: center; padding-top: 10px; font-size: 10pt; font-weight: bold;">EVENTLOG LTDA</div>
            <div style="flex: 1; border-top: 1px solid #000; text-align: center; padding-top: 10px; font-size: 10pt; font-weight: bold;">CONTRATANTE</div>
          </div>
        `,
        isTrash: false, 
        hideFromClient: false, 
        status: ContractStatus.ATIVO
      };
      onUpdateContracts([template]);
    }
  }, [contracts.length, onUpdateContracts]);

  const filteredContracts = useMemo(() => {
    return contracts.filter(c => {
      const client = clients.find(cli => cli.id === c.clientId);
      return (c.subject.toLowerCase().includes(search.toLowerCase()) ||
             (client?.empresa || '').toLowerCase().includes(search.toLowerCase()));
    });
  }, [contracts, search, clients]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20 px-6 md:px-12 lg:px-16 py-8">
      <style>{printStyles}</style>
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 px-2">
        <div>
          <h1 className="text-3xl font-black text-secondary uppercase tracking-tighter italic leading-none">Gestão de Contratos</h1>
          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mt-3 italic">Formalização Jurídica & Assinaturas Dinâmicas</p>
        </div>
        <button 
          onClick={() => setIsNewModalOpen(true)}
          className="w-full md:w-auto bg-primary text-white px-5 py-2.5 rounded-xl font-black uppercase text-[9px] tracking-widest shadow-lg shadow-primary/20 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2"
        >
          <Plus size={16} strokeWidth={3} /> Gerar Novo Contrato
        </button>
      </div>

      <Card className="overflow-hidden border-none shadow-xl bg-white rounded-[32px] mt-4">
        <div className="p-6 border-b border-slate-50 flex flex-col md:flex-row justify-between items-center gap-4">
           <div className="relative flex-1 w-full max-w-md group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-primary transition-colors" size={18} />
              <input 
                placeholder="Pesquisar contrato..." 
                className="w-full pl-12 pr-6 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-sm text-secondary focus:ring-4 focus:ring-primary/5 transition-all shadow-inner"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
           </div>
           <div className="flex items-center gap-2">
              <Badge className="bg-blue-50 text-blue-600 border-none font-black px-4 py-2">{filteredContracts.length} REGISTROS</Badge>
           </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50 text-slate-400 text-[10px] uppercase font-black tracking-widest border-b border-slate-100">
                <th className="px-8 py-5">Assunto / Documento</th>
                <th className="px-8 py-5">Cliente</th>
                <th className="px-8 py-5 text-center">Valor Total</th>
                <th className="px-8 py-5 text-center">Status</th>
                <th className="px-8 py-5 text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredContracts.map((c) => (
                <tr key={c.id} className="hover:bg-primary/[0.02] transition-colors group cursor-pointer" onClick={() => setSelectedContract(c)}>
                  <td className="px-8 py-6">
                    <p className="font-black text-secondary uppercase tracking-tight text-sm">{c.subject}</p>
                    <p className="text-[10px] text-gray-300 font-bold uppercase tracking-widest mt-1 italic">{c.projectName || 'Sem Projeto Atribuído'}</p>
                  </td>
                  <td className="px-8 py-6">
                    <span className="text-xs font-bold text-slate-500 uppercase">{clients.find(cli => cli.id === c.clientId)?.empresa || 'Template Base'}</span>
                  </td>
                  <td className="px-8 py-6 text-center font-black text-secondary">R$ {c.value.toLocaleString('pt-BR')}</td>
                  <td className="px-8 py-6 text-center">
                    <Badge className="bg-blue-50 text-blue-600 border-none">{c.status}</Badge>
                  </td>
                  <td className="px-8 py-6 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                       <button className="p-3 bg-gray-50 text-gray-400 hover:text-primary rounded-xl transition-all"><Eye size={18} /></button>
                       <button className="p-3 bg-gray-50 text-gray-400 hover:text-secondary rounded-xl transition-all"><Printer size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredContracts.length === 0 && (
                <tr>
                   <td colSpan={5} className="py-20 text-center opacity-30 italic font-black uppercase text-xs">Nenhum contrato localizado</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {isNewModalOpen && (
        <NewContractModal 
          clients={clients} 
          onClose={() => setIsNewModalOpen(false)} 
          onSave={(c: Contract) => { onUpdateContracts([c, ...contracts]); setIsNewModalOpen(false); }} 
        />
      )}

      {selectedContract && (
        <ContractViewModal 
          contract={selectedContract} 
          client={clients.find(cli => cli.id === selectedContract.clientId)}
          onClose={() => setSelectedContract(null)} 
        />
      )}
    </div>
  );
};

const ContractViewModal = ({ contract, client, onClose }: { contract: Contract, client?: Client, onClose: () => void }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  // Motor de substituição inteligente
  const renderedContent = useMemo(() => {
    let content = contract.description;
    const replacements: Record<string, string> = {
      '{{CLIENTE}}': client?.empresa || '___________________',
      '{{VALOR}}': `R$ ${contract.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      '{{ASSUNTO}}': contract.subject || '___________________',
      '{{DATA}}': new Date().toLocaleDateString('pt-BR'),
      '{{EMAIL}}': client?.emailContato || '___________________',
      '{{CELULAR}}': client?.telefonePrincipal || '___________________',
      '{{ENDERECO}}': client?.enderecoCompleto || '___________________',
      '{{DOCUMENTO}}': client?.cnpj_cpf || '___________________',
      '{{RESPONSAVEL}}': client?.contatoResponsavel || '___________________',
    };

    Object.keys(replacements).forEach(key => {
      const regex = new RegExp(key, 'g');
      content = content.replace(regex, replacements[key]);
    });

    return content;
  }, [contract, client]);

  const handlePrint = () => {
    setIsGenerating(true);
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Contrato - ${contract.subject}</title>
            <style>
              * { box-sizing: border-box; }
              html, body { 
                margin: 0 !important; 
                padding: 0 !important; 
                width: 100%;
                background-color: white;
              }
              body { 
                display: flex;
                justify-content: center;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              }
              .a4-document {
                width: 210mm;
                min-height: 297mm;
                padding: 25mm;
                margin: 0 auto; 
                background: white;
                color: black;
                line-height: 1.6;
                font-size: 11pt;
              }
              h1 { text-align: center; text-transform: uppercase; font-size: 18pt; margin-bottom: 30pt; }
              p { text-align: justify; margin-bottom: 12pt; }
              strong { font-weight: bold; }
              @media print {
                @page { size: A4; margin: 0; }
                body { margin: 0; padding: 0; width: 210mm; }
                .a4-document { margin: 0; width: 210mm; padding: 20mm; box-shadow: none; border: none; }
              }
            </style>
          </head>
          <body>
            <div class="a4-document">
              ${renderedContent}
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() { window.close(); };
                }, 700);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
      setIsGenerating(false);
    } else {
      alert("Bloqueador de pop-ups detectado.");
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-secondary/95 backdrop-blur-md z-[2000] flex flex-col md:p-10 animate-in fade-in overflow-hidden">
       <div className="no-print w-full max-w-5xl mx-auto flex justify-between items-center mb-8 px-4 shrink-0">
          <button onClick={onClose} className="flex items-center gap-3 text-white/40 hover:text-white transition-all font-black uppercase text-[10px] tracking-widest">
            <ArrowLeft size={20} /> Voltar para Lista
          </button>
          <div className="flex gap-4">
             <button 
               onClick={handlePrint}
               disabled={isGenerating}
               className="bg-primary text-white px-10 py-5 rounded-[24px] font-black uppercase text-xs tracking-widest flex items-center gap-3 shadow-2xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
             >
                {isGenerating ? <Loader2 size={20} className="animate-spin" /> : <FileDown size={20} strokeWidth={3} />}
                Gerar PDF / Imprimir
             </button>
             <button onClick={onClose} className="p-5 bg-white/5 hover:bg-red-500/20 text-white/40 hover:text-red-500 rounded-[24px] transition-all">
                <X size={24} />
             </button>
          </div>
       </div>

       <div className="flex-1 overflow-y-auto pb-20 px-4 scrollbar-hide">
          <div className="a4-preview-container">
            <div className="a4-preview animate-in zoom-in-95" dangerouslySetInnerHTML={{ __html: renderedContent }} />
          </div>
       </div>

       <div className="no-print fixed bottom-10 left-1/2 -translate-x-1/2 bg-[#0f172a] border border-white/10 px-8 py-4 rounded-full shadow-2xl flex items-center gap-4 z-50">
          <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
          <p className="text-[10px] font-black text-white/50 uppercase tracking-widest italic">Visualização Centralizada A4 • Documento Oficial</p>
       </div>
    </div>
  );
};

const NewContractModal = ({ clients, onClose, onSave }: any) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    clientId: '',
    subject: '',
    value: '',
    type: 'Locação',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    description: `
      <h1 style="text-align: center;">INSTRUMENTO PARTICULAR DE LOCAÇÃO</h1>
      <p>Pelo presente instrumento, o cliente <strong>{{CLIENTE}}</strong> contrata <strong>{{ASSUNTO}}</strong>...</p>
      <p>Local de Prestação: <strong>{{ENDERECO}}</strong></p>
    `,
    isTrash: false,
    hideFromClient: false
  });

  const execCommand = (cmd: string) => { document.execCommand(cmd, false); };
  const insertVariable = (v: string) => { 
    if (editorRef.current) { 
      editorRef.current.focus(); 
      document.execCommand('insertHTML', false, `{{${v}}}`); 
    } 
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newContract: Contract = {
      id: Date.now().toString(),
      ...formData,
      description: editorRef.current?.innerHTML || '',
      value: parseFloat(formData.value || '0'),
      status: ContractStatus.ATIVO
    };
    onSave(newContract);
  };

  return (
    <div className="fixed inset-0 bg-secondary/90 backdrop-blur-md z-[500] flex items-center justify-center p-4">
      <div className="bg-white rounded-[48px] w-full max-w-6xl shadow-2xl overflow-hidden animate-in zoom-in-95 flex flex-col h-[90vh]">
        <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-white shrink-0">
           <div className="flex items-center gap-4">
              <div className="p-3 bg-primary rounded-2xl text-white shadow-lg"><FileText size={24} /></div>
              <div>
                <h2 className="text-xl font-black uppercase text-secondary">Novo Documento</h2>
                <p className="text-[9px] font-bold text-primary uppercase tracking-[0.3em]">Editor de Minutas Inteligentes</p>
              </div>
           </div>
           <button onClick={onClose} className="p-3 hover:bg-red-50 text-slate-300 hover:text-red-500 rounded-2xl transition-all"><X size={28} /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-10 space-y-8 bg-white scrollbar-hide">
           <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-1.5">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Cliente Solicitante</label>
                 <select required className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary" value={formData.clientId} onChange={e => setFormData({...formData, clientId: e.target.value})}>
                    <option value="">Selecionar Cliente...</option>
                    {clients.map((c: any) => <option key={c.id} value={c.id}>{c.empresa}</option>)}
                 </select>
              </div>
              <div className="space-y-1.5">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Assunto / Título</label>
                 <input required className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary" placeholder="Ex: Locação Festival Outono" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} />
              </div>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="space-y-1.5">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Valor do Contrato (R$)</label>
                 <input required type="number" step="0.01" className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-black text-secondary" value={formData.value} onChange={e => setFormData({...formData, value: e.target.value})} />
              </div>
              <div className="space-y-1.5">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Data Início</label>
                 <input type="date" className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} />
              </div>
              <div className="space-y-1.5">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Data Fim (Previsão)</label>
                 <input type="date" className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary" value={formData.endDate} onChange={e => setFormData({...formData, endDate: e.target.value})} />
              </div>
           </div>

           <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-gray-50 pb-2">
                 <label className="text-[10px] font-black text-secondary uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Sparkles size={14} className="text-primary" /> Conteúdo Jurídico Dinâmico
                 </label>
              </div>
              
              <div className="flex flex-wrap gap-2 mb-4 bg-slate-50 p-4 rounded-[24px] border border-slate-100">
                <p className="w-full text-[8px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Clique para inserir no texto:</p>
                <VariableBtn icon={UserCheck} label="Cliente" tag="CLIENTE" onClick={() => insertVariable('CLIENTE')} />
                <VariableBtn icon={CreditCard} label="Documento" tag="DOCUMENTO" onClick={() => insertVariable('DOCUMENTO')} />
                <VariableBtn icon={Mail} label="E-mail" tag="EMAIL" onClick={() => insertVariable('EMAIL')} />
                <VariableBtn icon={Smartphone} label="Celular" tag="CELULAR" onClick={() => insertVariable('CELULAR')} />
                <VariableBtn icon={MapPin} label="Endereço" tag="ENDERECO" onClick={() => insertVariable('ENDERECO')} />
                <VariableBtn icon={UserCheck} label="Responsável" tag="RESPONSAVEL" onClick={() => insertVariable('RESPONSAVEL')} />
                <div className="w-[1px] h-6 bg-slate-200 mx-2 hidden md:block"></div>
                <VariableBtn icon={DollarSign} label="Valor" tag="VALOR" onClick={() => insertVariable('VALOR')} />
                <VariableBtn icon={Calendar} label="Data" tag="DATA" onClick={() => insertVariable('DATA')} />
              </div>

              <div className="border-2 border-slate-100 rounded-[32px] overflow-hidden bg-white shadow-xl">
                 <div className="bg-slate-50 border-b border-slate-100 p-3 flex gap-2">
                    <EditorBtn icon={Bold} onClick={() => execCommand('bold')} />
                    <EditorBtn icon={Italic} onClick={() => execCommand('italic')} />
                    <EditorBtn icon={Underline} onClick={() => execCommand('underline')} />
                    <div className="w-[1px] h-6 bg-slate-200 mx-2"></div>
                    <EditorBtn icon={AlignLeft} onClick={() => execCommand('justifyLeft')} />
                    <EditorBtn icon={AlignCenter} onClick={() => execCommand('justifyCenter')} />
                    <EditorBtn icon={AlignRight} onClick={() => execCommand('justifyRight')} />
                 </div>
                 <div 
                   ref={editorRef}
                   contentEditable 
                   className="w-full p-10 min-h-[500px] outline-none prose prose-blue max-w-none text-slate-700 font-medium leading-relaxed"
                   dangerouslySetInnerHTML={{ __html: formData.description }}
                 />
              </div>
           </div>

           <div className="pt-8 border-t border-slate-50 flex justify-end gap-4">
              <button type="button" onClick={onClose} className="px-8 py-5 border-2 border-slate-100 text-slate-400 rounded-3xl font-black uppercase text-[10px] tracking-widest hover:bg-gray-50">Cancelar</button>
              <button type="submit" className="px-14 py-5 bg-secondary text-white rounded-[28px] font-black uppercase text-xs tracking-widest shadow-2xl hover:bg-black transition-all flex items-center justify-center gap-3">
                 <Save size={18} /> Salvar e Registrar Contrato
              </button>
           </div>
        </form>
      </div>
    </div>
  );
};

const EditorBtn = ({ icon: Icon, onClick }: any) => (
  <button type="button" onClick={onClick} className="p-2.5 bg-white border border-slate-200 text-slate-400 hover:text-primary rounded-xl transition-all shadow-sm active:scale-90">
    <Icon size={16} />
  </button>
);

const VariableBtn = ({ label, icon: Icon, onClick, tag }: any) => (
  <button 
    type="button" 
    onClick={onClick} 
    className="bg-white text-secondary border border-slate-200 px-4 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center gap-2 hover:border-primary hover:text-primary hover:shadow-md transition-all active:scale-95 group"
    title={`Inserir {{${tag}}}`}
  >
    <Icon size={12} className="text-primary group-hover:scale-110 transition-transform" /> {label}
  </button>
);
